import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc, updateDoc, getDocs, collection, where, query, arrayRemove } from 'firebase/firestore';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Swal from 'sweetalert2';

export default function EditarProyecto() {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const navigate = useNavigate();

  const [proyecto, setProyecto] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [propiedades, setPropiedades] = useState<any[]>([]);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    area_m2: '',
    amenidades: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProyecto = async () => {
      if (!id || !user) return;

      try {
        const snap = await getDoc(doc(db, 'proyectos', id));
        if (!snap.exists()) return;

        const data = snap.data();
        setProyecto(data);
        setForm({
          nombre: data.nombre || '',
          descripcion: data.descripcion || '',
          direccion: data.direccion || '',
          area_m2: data.area_m2 || '',
          amenidades: (data.amenidades || []).join(', ')
        });

        if (data.inmobiliaria_id) {
          const inmoSnap = await getDocs(
            query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', data.inmobiliaria_id))
          );
          if (!inmoSnap.empty) {
            setInmobiliaria(inmoSnap.docs[0].data());
          }
        }

        if (Array.isArray(data.propiedad_id) && data.propiedad_id.length > 0) {
          const propSnap = await getDocs(query(
            collection(db, 'propiedades'),
            where('propiedad_id', 'in', data.propiedad_id.slice(0, 10))
          ));
          setPropiedades(propSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error al cargar proyecto:', err);
        setLoading(false);
      }
    };

    fetchProyecto();
  }, [id, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, 'proyectos', id!), {
        nombre: form.nombre,
        descripcion: form.descripcion,
        direccion: form.direccion,
        area_m2: Number(form.area_m2),
        amenidades: form.amenidades.split(',').map(a => a.trim())
      });
  
      Swal.fire({
        icon: 'success',
        title: 'Cambios guardados',
        text: 'El proyecto fue actualizado correctamente',
        confirmButtonColor: '#3085d6'
      }).then(() => {
        navigate('/admin-project');
      });
  
    } catch (error) {
      console.error('Error al actualizar el proyecto:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron guardar los cambios. Intenta nuevamente.',
        confirmButtonColor: '#d33'
      });
    }
  };
  

  const handleEliminarPropiedad = async (propiedadId: string) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar propiedad del proyecto?',
      text: 'Esta acción solo la desvinculará del proyecto. La propiedad no se eliminará de la base de datos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    });
  
    if (!confirm.isConfirmed) return;
  
    try {
      await updateDoc(doc(db, 'proyectos', id!), {
        propiedad_id: arrayRemove(propiedadId)
      });
  
      setPropiedades(prev => prev.filter(p => p.propiedad_id !== propiedadId));
  
      Swal.fire({
        icon: 'success',
        title: 'Propiedad eliminada del proyecto',
        text: 'La propiedad fue desvinculada correctamente.',
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error('Error al eliminar propiedad:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: 'No se pudo eliminar la propiedad del proyecto.',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading) return <div className="container py-5 text-center">Cargando...</div>;

  return (
    <>
      <UserNav />
      <div className="container mt-5">
        <h2>Editar Proyecto</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Nombre</label>
            <input type="text" className="form-control" name="nombre" value={form.nombre} onChange={handleChange} required />
          </div>

          <div className="mb-3">
            <label className="form-label">Descripción</label>
            <textarea className="form-control" name="descripcion" rows={3} value={form.descripcion} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label className="form-label">Dirección</label>
            <input type="text" className="form-control" name="direccion" value={form.direccion} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label className="form-label">Área (m²)</label>
            <input type="number" className="form-control" name="area_m2" value={form.area_m2} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label className="form-label">Amenidades (separadas por coma)</label>
            <input type="text" className="form-control" name="amenidades" value={form.amenidades} onChange={handleChange} />
          </div>

          <p><strong>Inmobiliaria:</strong> {inmobiliaria?.nombre || 'No disponible'}</p>

          <button type="submit" className="btn btn-primary">Guardar Cambios</button>
        </form>

        <hr />
        <h4>Propiedades vinculadas ({propiedades.length})</h4>
        <div className="row">
          {propiedades.map(p => (
            <div className="col-md-4 mb-3" key={p.id}>
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">{p.nombre}</h5>
                  <p>{p.tipo} - ${p.precio}</p>
                  <a className="btn btn-sm btn-outline-primary me-2" href={`/ver-propiedad-admin/${p.id}`}>
                    Ver propiedad
                  </a>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarPropiedad(p.propiedad_id)}>
                    Quitar del proyecto
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
